import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/data";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/AdminLayout.jsx";

const client = generateClient();
const emptyNews = { mensaje: "", peso: 0, activa: true };
const speedKey = "noticiasVelocidad";

const sortNews = (items) =>
  [...items].sort(
    (first, second) =>
      second.peso - first.peso ||
      new Date(first.createdAt) - new Date(second.createdAt),
  );

export default function NoticiasAdmin() {
  const [news, setNews] = useState([]);
  const [form, setForm] = useState(emptyNews);
  const [editingId, setEditingId] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [speedParameterId, setSpeedParameterId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSpeed, setSavingSpeed] = useState(false);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const [newsResult, settingsResult] = await Promise.all([
          client.models.Noticia.list({ authMode: "userPool" }),
          client.models.Parametro.list({
            authMode: "userPool",
            filter: { clave: { eq: speedKey } },
          }),
        ]);
        setNews(sortNews(newsResult.data));
        const speedParameter = settingsResult.data[0];
        const configuredSpeed = Number(speedParameter?.valor);
        if (configuredSpeed >= 1 && configuredSpeed <= 10) {
          setSpeed(configuredSpeed);
        }
        setSpeedParameterId(speedParameter?.id ?? null);
      } catch (error) {
        console.error("Error al obtener las noticias:", error);
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, []);

  const resetForm = () => {
    setForm(emptyNews);
    setEditingId(null);
  };

  const saveNews = async (event) => {
    event.preventDefault();
    const mensaje = form.mensaje.trim();
    if (!mensaje) return;

    setSaving(true);
    try {
      const payload = { mensaje, peso: Number(form.peso), activa: form.activa };
      const result = editingId
        ? await client.models.Noticia.update(
            { id: editingId, ...payload },
            { authMode: "userPool" },
          )
        : await client.models.Noticia.create(payload, { authMode: "userPool" });

      if (result.data) {
        setNews((currentNews) =>
          sortNews([
            ...currentNews.filter((item) => item.id !== result.data.id),
            result.data,
          ]),
        );
        resetForm();
        window.dispatchEvent(new Event("news-updated"));
      }
    } catch (error) {
      console.error("Error al guardar la noticia:", error);
      alert("No fue posible guardar la noticia.");
    } finally {
      setSaving(false);
    }
  };

  const editNews = (item) => {
    setForm({ mensaje: item.mensaje, peso: item.peso, activa: item.activa });
    setEditingId(item.id);
  };

  const deleteNews = async (id) => {
    if (!window.confirm("¿Deseas eliminar esta noticia?")) return;

    try {
      await client.models.Noticia.delete({ id }, { authMode: "userPool" });
      setNews((currentNews) => currentNews.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
      window.dispatchEvent(new Event("news-updated"));
    } catch (error) {
      console.error("Error al eliminar la noticia:", error);
      alert("No fue posible eliminar la noticia.");
    }
  };

  const saveSpeed = async (event) => {
    event.preventDefault();
    const value = Math.min(10, Math.max(1, Number(speed) || 1));
    setSavingSpeed(true);

    try {
      const payload = { clave: speedKey, valor: String(value) };
      const result = speedParameterId
        ? await client.models.Parametro.update(
            { id: speedParameterId, ...payload },
            { authMode: "userPool" },
          )
        : await client.models.Parametro.create(payload, {
            authMode: "userPool",
          });

      if (result.data) {
        setSpeed(value);
        setSpeedParameterId(result.data.id);
        window.dispatchEvent(new Event("news-updated"));
      }
    } catch (error) {
      console.error("Error al guardar la velocidad:", error);
      alert("No fue posible guardar la velocidad.");
    } finally {
      setSavingSpeed(false);
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="mx-auto max-w-5xl p-4 md:p-8">
          <h2 className="text-2xl font-bold text-sky-950">Noticias</h2>
          <p className="mt-1 text-gray-600">
            Los titulares activos aparecen en el cintillo. Un peso mayor se
            muestra primero.
          </p>

          <form
            className="mt-6 flex flex-wrap items-end gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4"
            onSubmit={saveSpeed}
          >
            <label className="block">
              <span className="mb-1 block font-medium text-amber-950">
                Velocidad del cintillo
              </span>
              <input
                className="w-32 rounded border border-amber-300 bg-white p-2"
                max="10"
                min="1"
                onChange={(event) => setSpeed(event.target.value)}
                type="number"
                value={speed}
              />
            </label>
            <p className="max-w-md text-sm text-amber-900">
              El valor 1 usa la velocidad base. Un número mayor hace que los
              titulares avancen más rápido.
            </p>
            <button
              className="rounded bg-amber-600 px-4 py-2 font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={savingSpeed}
              type="submit"
            >
              {savingSpeed ? "Guardando..." : "Guardar velocidad"}
            </button>
          </form>

          <form
            className="mt-6 rounded-lg border border-sky-100 bg-sky-50 p-4 shadow-sm"
            onSubmit={saveNews}
          >
            <div className="grid gap-4 md:grid-cols-[1fr_9rem]">
              <label className="block">
                <span className="mb-1 block font-medium text-sky-950">
                  Titular
                </span>
                <input
                  className="w-full rounded border border-sky-200 bg-white p-2"
                  maxLength="240"
                  onChange={(event) =>
                    setForm({ ...form, mensaje: event.target.value })
                  }
                  placeholder="Escribe la noticia que aparecerá en el cintillo"
                  required
                  value={form.mensaje}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-medium text-sky-950">Peso</span>
                <input
                  className="w-full rounded border border-sky-200 bg-white p-2"
                  onChange={(event) =>
                    setForm({ ...form, peso: event.target.value })
                  }
                  type="number"
                  value={form.peso}
                />
              </label>
            </div>
            <label className="mt-4 flex w-fit items-center gap-2 text-sm font-medium text-sky-950">
              <input
                checked={form.activa}
                onChange={(event) =>
                  setForm({ ...form, activa: event.target.checked })
                }
                type="checkbox"
              />
              Mostrar esta noticia
            </label>
            <div className="mt-4 flex gap-3">
              <button
                className="rounded bg-sky-700 px-4 py-2 font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
                type="submit"
              >
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Agregar noticia"}
              </button>
              {editingId && (
                <button
                  className="rounded border border-sky-700 px-4 py-2 font-semibold text-sky-800 hover:bg-white"
                  onClick={resetForm}
                  type="button"
                >
                  Cancelar
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 overflow-hidden rounded-lg border">
            <table className="w-full text-left text-sm">
              <thead className="bg-sky-950 text-white">
                <tr>
                  <th className="px-4 py-3">Orden</th>
                  <th className="px-4 py-3">Titular</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-4" colSpan="4">
                      Cargando noticias...
                    </td>
                  </tr>
                ) : news.length ? (
                  news.map((item, index) => (
                    <tr className="border-t" key={item.id}>
                      <td className="px-4 py-3">
                        {index + 1} <span className="text-gray-500">({item.peso})</span>
                      </td>
                      <td className="px-4 py-3">{item.mensaje}</td>
                      <td className="px-4 py-3">
                        {item.activa ? "Activa" : "Oculta"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3 font-semibold">
                          <button
                            className="text-sky-700 hover:text-sky-950"
                            onClick={() => editNews(item)}
                            type="button"
                          >
                            Editar
                          </button>
                          <button
                            className="text-rose-700 hover:text-rose-950"
                            onClick={() => deleteNews(item.id)}
                            type="button"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-4 text-gray-600" colSpan="4">
                      Aún no hay noticias registradas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AuthInterceptor>
    </Layout>
  );
}
